/**
 * The keys with typed controls (ORDR-119, seam C4 of w2c-ord-config).
 *
 * Only keys whose rules are FROZEN get a control; every other key is edited
 * as JSON. Each rule here is the server's own, applied client-side so an
 * obviously bad value never leaves the browser. The server still decides.
 *
 * ABSENT ALWAYS MEANS THE READER'S DEFAULT, and each rule says what that is,
 * because "Remove this key" is a real edit and the person should know what it
 * does.
 *
 * `PREP_TIMES.delay_minutes` is typed as an integer >= 0. Seam C4 names the
 * key without freezing its rule; HQ ruled (C-HQ3) that it IS FR-ORD-020's
 * temporary delay, and the kitchen needs a number control for it. The bound is
 * this lane's reading and is flagged for HQ.
 *
 * BRAND_THEME's keys are TEXT (ORDR-175), from paio-ordering `services/brand.py`
 * at `679e6fd`. The colours mirror the server EXACTLY: `#` and six hex digits,
 * `isBrandHex` being the same pattern the app's own brand read applies. The
 * display name checks only the 1-to-80 length; "printable" is Python's
 * `str.isprintable`, which JavaScript has no faithful copy of, so the server
 * decides it. The logo URL is the SERVER'S ALONE: its hosts are a server
 * setting (`BRAND_LOGO_HOSTS`) this app cannot see, so the control takes any
 * URL and the PUT's INVALID_VALUE is shown beside it. A client rule is only
 * ever weaker than the server's, never stricter: refusing what the server
 * accepts would be the lie the other way round.
 */

import { isBrandHex } from "@pirouz-ca/ui-tokens";
import type { ConfigDomain } from "./domains.js";

export interface FieldRule {
  readonly key: string;
  readonly label: string;
  readonly min?: number;
  readonly max?: number;
  /** Set for a choice; absent for an integer. */
  readonly choices?: readonly { readonly value: string; readonly label: string }[];
  /** Set for a text key (ORDR-175); absent for an integer or a choice. */
  readonly text?: "colour" | "name" | "url";
  readonly absentMeans: string;
}

const int = (key: string, label: string, min: number, absentMeans: string, max?: number): FieldRule =>
  max === undefined ? { key, label, min, absentMeans } : { key, label, min, max, absentMeans };

export const TYPED_FIELDS: Readonly<Partial<Record<ConfigDomain, readonly FieldRule[]>>> = {
  CAPACITY: [
    int("capacity_coefficient_permille", "Capacity (per mille, 1000 = 100%)", 0, "1000 (100%)"),
    {
      key: "acceptance_policy",
      label: "Accepting orders",
      choices: [
        { value: "ACKNOWLEDGED", label: "Staff accept each order" },
        { value: "AUTOMATIC", label: "Accept automatically" },
      ],
      absentMeans: "Staff accept each order",
    },
    int("clearance_rate_override_per_hour", "Clearance rate override (orders per hour)", 1, "the measured rate"),
    int("quote_target_coverage_permille", "Quote coverage target (per mille)", 1, "the service default", 1000),
    int("quote_target_padding_seconds", "Quote padding (seconds)", 0, "the service default"),
  ],
  HOURS: [int("closing_soon_minutes", "Closing-soon warning (minutes)", 0, "15")],
  PREP_TIMES: [int("delay_minutes", "Declared kitchen delay (minutes)", 0, "no delay")],
  BRAND_THEME: [
    { key: "display_name", label: "Display name", text: "name", max: 80, absentMeans: "no brand name is shown" },
    { key: "logo_url", label: "Logo URL", text: "url", absentMeans: "no logo is shown" },
    { key: "primary", label: "Primary colour", text: "colour", absentMeans: "the neutral look" },
    { key: "accent", label: "Accent colour", text: "colour", absentMeans: "the neutral look" },
  ],
};

export type FieldCheck =
  | { readonly ok: true; readonly value: number | string }
  | { readonly ok: false; readonly message: string };

/** Parse what was typed against the frozen rule. */
export function checkField(rule: FieldRule, raw: string): FieldCheck {
  if (rule.text === "colour") {
    return isBrandHex(raw)
      ? { ok: true, value: raw }
      : { ok: false, message: "Write a colour as # and exactly six hex digits, such as #1A2B3C." };
  }
  if (rule.text === "name") {
    const length = [...raw].length;
    return raw.trim() !== "" && (rule.max === undefined || length <= rule.max)
      ? { ok: true, value: raw }
      : { ok: false, message: `Must be 1 to ${String(rule.max)} characters.` };
  }
  if (rule.text === "url") {
    return { ok: true, value: raw };
  }
  if (rule.choices !== undefined) {
    return rule.choices.some((c) => c.value === raw)
      ? { ok: true, value: raw }
      : { ok: false, message: "Choose one of the options." };
  }
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    return { ok: false, message: "Enter a whole number." };
  }
  const value = Number(trimmed);
  if (rule.min !== undefined && value < rule.min) {
    return { ok: false, message: `Must be at least ${String(rule.min)}.` };
  }
  if (rule.max !== undefined && value > rule.max) {
    return { ok: false, message: `Must be at most ${String(rule.max)}.` };
  }
  return { ok: true, value };
}
